import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type TextScale = 'normal' | 'large' | 'xl';

interface AccessibilityContextType {
  textScale: TextScale;
  setTextScale: (scale: TextScale) => void;
  // Helper to get classes based on current scale
  getScaledText: (baseClass: string) => string;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [textScale, setTextScaleState] = useState<TextScale>('normal');

  useEffect(() => {
    const savedScale = localStorage.getItem('accessibility-text-scale') as TextScale;
    if (savedScale && ['normal', 'large', 'xl'].includes(savedScale)) {
      setTextScaleState(savedScale);
    }
  }, []);

  const setTextScale = (scale: TextScale) => {
    setTextScaleState(scale);
    localStorage.setItem('accessibility-text-scale', scale);
  };

  const getScaledText = (baseClass: string): string => {
    if (textScale === 'normal') return baseClass;

    // Simple mapping for common Tailwind text classes
    // This can be expanded as needed
    const mappings: Record<string, Record<TextScale, string>> = {
      'text-xs': { normal: 'text-xs', large: 'text-sm', xl: 'text-base' },
      'text-sm': { normal: 'text-sm', large: 'text-base', xl: 'text-lg' },
      'text-base': { normal: 'text-base', large: 'text-lg', xl: 'text-xl' },
      'text-lg': { normal: 'text-lg', large: 'text-xl', xl: 'text-2xl' },
      'text-xl': { normal: 'text-xl', large: 'text-2xl', xl: 'text-3xl' },
    };

    // Extract the size part (e.g., 'text-xs')
    const match = baseClass.match(/text-(xs|sm|base|lg|xl|2xl|3xl|4xl)/);
    if (match) {
      const sizeClass = match[0];
      const mapping = mappings[sizeClass];
      if (mapping) {
        return baseClass.replace(sizeClass, mapping[textScale]);
      }
    }

    return baseClass;
  };

  return (
    <AccessibilityContext.Provider value={{ textScale, setTextScale, getScaledText }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
